import { motion } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { useInView } from "framer-motion";

import Section from "@/components/layout/Section";
import Container from "@/components/layout/Container";

/* ================= COUNTER ================= */

function Counter({ end, suffix = "", trigger }) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!trigger) return;

    let start = 0;
    const duration = 1200;
    const stepTime = 16;
    const steps = duration / stepTime;
    const increment = end / steps;

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setValue(end);
        clearInterval(timer);
      } else {
        setValue(parseFloat(start.toFixed(1)));
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [end, trigger]);

  return (
    <>
      {value}
      {suffix}
    </>
  );
}

/* ================= DATA ================= */

const stats = [
  { value: 1200, suffix: "+", label: "Guests Hosted" },
  { value: 32, suffix: "%", label: "Revenue Uplift" },
  { value: 4.8, suffix: "★", label: "Average Rating" },
  { value: 24, suffix: "/7", label: "Guest Support" },
  { value: 92, suffix: "%", label: "Occupancy Rate" },
  { value: 85, suffix: "%", label: "5 Star Reviews" },
];

/* ================= MAIN ================= */

export default function SolutionsExperience() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  /* AUTO LOOP (STABLE) */
  useEffect(() => {
    if (paused) return;

    const i = setInterval(() => {
      setActive((v) => (v + 1) % stats.length);
    }, 2600);

    return () => clearInterval(i);
  }, [paused]);

  return (
    <Section background="bg-bg-surface" padding="lg">
      <Container>

        <div
          ref={ref}
          className="
            flex flex-col gap-12
            md:grid md:grid-cols-2 md:gap-16
            items-center
          "
        >

          {/* 🔥 CONTENT */}
          <div className="order-1 md:order-2">

            <h2 className="text-heading md:text-display font-extrabold text-text-primary max-w-[18ch] leading-tight">
              Built on real execution
            </h2>

            <p className="mt-4 text-body text-text-muted max-w-reading">
              Zenia is structured around real-world property operations.
              Every system reflects actual performance not assumptions.
            </p>

            <div className="mt-6 space-y-2 text-body text-text-muted">
              <p>• No inflated projections</p>
              <p>• Built for consistency</p>
              <p>• Long-term revenue focus</p>
            </div>

          </div>

          {/* 🔥 STATS */}
          <div
            className="order-2 md:order-1 grid grid-cols-2 gap-4 md:gap-5"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >

            {stats.map((item, i) => {
              const isActive = i === active;

              return (
                <motion.div
                  key={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`
                    relative rounded-xl border p-4 md:p-5 overflow-hidden
                    transition-all duration-normal

                    ${isActive
                      ? "border-brand-primary bg-bg-light shadow-card"
                      : "border-border-soft bg-bg-surface"}
                  `}
                  animate={{
                    scale: isActive ? 1.04 : 1,
                  }}
                  transition={{ duration: 0.25 }}
                >

                  {/* PROGRESS */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-[2px] bg-brand-primary"
                    initial={{ width: "0%" }}
                    animate={{ width: isActive ? "100%" : "0%" }}
                    transition={{ duration: 2.6, ease: "linear" }}
                  />

                  {/* GLOW */}
                  <motion.div
                    className="absolute inset-0 bg-brand-light"
                    animate={{ opacity: isActive ? 1 : 0 }}
                    transition={{ duration: 0.25 }}
                  />

                  <div className="relative z-10">

                    <div className="text-[24px] md:text-[28px] font-extrabold text-text-primary leading-none">
                      <Counter
                        end={item.value}
                        suffix={item.suffix}
                        trigger={inView}
                      />
                    </div>

                    <div className="mt-1 text-small text-text-muted">
                      {item.label}
                    </div>

                  </div>

                </motion.div>
              );
            })}

          </div>

        </div>

      </Container>
    </Section>
  );
}